import { buildCloudFrontUrl } from '@/utils/pathUtils';
import { uploadFileToS3 } from '@/utils/s3Utils';
import store from '@/store';

(function () {
    'ngInject';

    angular.module('app.shared.services').factory('FontManagerService', fontManagerService);

    function fontManagerService(FontAPIService, $q, blockUI, $mdDialog) {
        let currentFonts = [];
        let defaultFontIndex = 0;
        let fontLoadingPromise = null;
        const maxFontsToDownload = 20;

        function getAllFonts(forceReload) {
            return new Promise(function (resolve, reject) {
                if (!forceReload && fontLoadingPromise && getLoadedFonts() && getLoadedFonts().length) {
                    resolve(getLoadedFonts());
                } else {
                    FontAPIService.getFonts()
                        .then(function (fonts) {
                            let sortedFonts = fonts;
                            if (fonts && fonts.length) {
                                sortedFonts = fonts.sort((a, b) => a.Name.localeCompare(b.Name));
                            }
                            currentFonts = sortedFonts;
                            store.commit('userFonts', sortedFonts);
                            resolve(sortedFonts);
                        })
                        .catch(function (e) {
                            reject(e);
                        });
                }
            });
        }

        function getLoadedFonts() {
            return currentFonts;
        }

        function initFonts(forceReload) {
            // If we are already loading fonts, just return the same promise
            if (forceReload || !fontLoadingPromise) {
                console.log('Initializing Fonts');
                fontLoadingPromise = new Promise(async (resolve, reject) => {
                    try {
                        await getAllFonts(forceReload);

                        // Load only the Arial font by default
                        const index = currentFonts.findIndex((font) => font.Name.toLowerCase() === 'arial');

                        if (index > -1) {
                            defaultFontIndex = index;
                            // Add the font to the opentype cache
                            const name = currentFonts[index].Name + '_' + currentFonts[index].Style;
                            Font.loadAsync(name, buildCloudFrontUrl(currentFonts[index].Path))
                                .then(function () {
                                    console.log('Fonts Initialized');
                                    resolve(getLoadedFonts());
                                })
                                .catch(function (e) {
                                    reject(e);
                                });
                        } else {
                            reject(new Error('Cannot find Arial font'));
                        }
                    } catch (error) {
                        reject(error);
                    }
                });
                return fontLoadingPromise;
            } else {
                return fontLoadingPromise;
            }
        }

        function getDefaultFont() {
            return currentFonts[defaultFontIndex];
        }

        function loadFontFromPath(fontPath) {
            return $q(function (resolve, reject) {
                // Get font from the font path
                var font = currentFonts.find(function (font) {
                    return font.Path === fontPath;
                });

                if (font) {
                    var fontName = font.Name + '_' + font.Style;

                    // If font is already cached just return it
                    if (Font.hasFont(fontName)) {
                        resolve(fontName);
                    } else {
                        // Otherwise load the font file
                        Font.loadAsync(fontName, buildCloudFrontUrl(font.Path))
                            .then(function () {
                                resolve(fontName);
                            })
                            .catch(function (e) {
                                reject(e);
                            });
                    }
                } else {
                    const error = new Error("Couldn't find font with path " + fontPath);
                    console.error(error);
                    reject(error);
                }
            });
        }

        /**
         * Upload a font to the given user
         * @param {File} file font file to upload
         * @param {Function} progressCallback callback executed to update the upload progress
         * @returns {Promise}
         */
        function uploadFont(file, progressCallback) {
            return new Promise(async (resolve, reject) => {
                try {
                    console.log('Uploading font file: ' + file.name);

                    // Get the signed URL and upload the font to S3
                    const response = await FontAPIService.getFontUploadSignedUrl(file.name);
                    if (progressCallback) progressCallback(0);
                    await uploadFileToS3(response.SignedUrl, response.Mime, file);
                    if (progressCallback) progressCallback(100);

                    // Validate the font
                    await FontAPIService.validateFont(response.Name);

                    console.log(`Font ${file.name} uploaded!`);
                    resolve();
                } catch (error) {
                    reject(error);
                } finally {
                    fontLoadingPromise = null;
                }
            });
        }

        function showUsedFontItems(errorText, list = null, type = null) {
            var plainHTML = '<p><b> ' + errorText + ' </b></p>';

            if (list && type) {
                plainHTML =
                    plainHTML +
                    '<table class="font-delete-table" width="100%" border="0">' +
                    '  <thead>' +
                    '   <td>' +
                    type +
                    ' Id</td>' +
                    '   <td>Name</td>' +
                    '  </thead>' +
                    '  <tbody>';

                for (var i = 0; i < list.length; i++) {
                    plainHTML =
                        plainHTML +
                        '    <tr>' +
                        '       <td class="product-id">' +
                        list[i].id +
                        '</td>' +
                        '       <td>' +
                        list[i].name +
                        '</td>' +
                        '    </tr>';
                }

                plainHTML = plainHTML + '  </tbody>' + '</table>';
            }

            $mdDialog.show(
                $mdDialog
                    .alert()
                    .clickOutsideToClose(false)
                    .multiple(true)
                    .title('Font in use!')
                    .htmlContent(plainHTML)
                    .ok('OK')
            );
        }

        function deleteFont(font) {
            return new Promise((resolve, reject) => {
                var fontName = font.Path.substr(font.Path.lastIndexOf('/') + 1, font.Path.length);
                $mdDialog
                    .show(
                        $mdDialog
                            .confirm()
                            .clickOutsideToClose(false)
                            .multiple(true)
                            .title('Confirmation')
                            .htmlContent('<p>Are you sure you want to delete the font ' + fontName + '?</p>')
                            .ok('OK')
                            .cancel('Cancel')
                    )
                    .then(async function () {
                        blockUI.start('Deleting font');
                        await FontAPIService.deleteFont(fontName)
                            .then(function () {
                                fontLoadingPromise = null;
                                blockUI.stop();
                                console.log('Deleted font ' + fontName);
                                $mdDialog
                                    .show(
                                        $mdDialog
                                            .alert()
                                            .clickOutsideToClose(false)
                                            .multiple(true)
                                            .title('Font deleted!')
                                            .htmlContent('<p>The font ' + fontName + ' was deleted!</p>')
                                            .ok('OK')
                                    )
                                    .then(function () {
                                        resolve(true);
                                    });
                            })
                            .catch(function (err) {
                                if (err.status === 409)
                                    showUsedFontItems(err.data.errorText, err.data.list, err.data.type);
                                fontLoadingPromise = null;
                                blockUI.stop();
                                reject(err);
                            });
                    });
            });
        }

        function clearFontsCache() {
            currentFonts = [];
            fontLoadingPromise = null;
            Font.clearCache();
        }

        //checks if a font exists (courtesy of Carlos Cespedes) by rendering a text in fontName
        //and comparing it's width with the width of the text render using the default font
        function doesFontExist(fontName) {
            var canvas = document.createElement('canvas');
            var context = canvas.getContext('2d');
            var text = 'abcdefghijklmnopqrstuvwxyz0123456789';
            context.font = '72px monospace';
            var baselineSize = context.measureText(text).width;
            context.font = "72px '" + fontName + "', monospace";
            var newSize = context.measureText(text).width;
            canvas = null;
            //
            // If the size of the two text instances is the same, the font does not exist because it is being rendered
            // using the default sans-serif font
            //
            if (newSize == baselineSize) {
                return false;
            } else {
                return true;
            }
        }

        function getMaxFontsToDownload() {
            return maxFontsToDownload;
        }

        return {
            getAllFonts: getAllFonts,
            getLoadedFonts: getLoadedFonts,
            initFonts: initFonts,
            getDefaultFont: getDefaultFont,
            loadFontFromPath: loadFontFromPath,
            deleteFont: deleteFont,
            uploadFont: uploadFont,
            clearFontsCache: clearFontsCache,
            getMaxFontsToDownload: getMaxFontsToDownload,
        };
    }
})();
